/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState, useCallback } from "react";
import {
    Alert,
    AlertTitle,
    Backdrop,
    Box,
    Button,
    IconButton,
    Modal,
    Fade,
    Link,
    Stack,
    Paper,
    useTheme,
    lighten,
    darken,
    Divider,
    Typography,
    TextField,
    Tooltip,
    CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDropzone, FileWithPath } from "react-dropzone";
import { ModalBase } from "../../utils/Interfaces";
import ConditionalBox from "../../components/controls/ConditionalBox";
import { uploadPDFWorkOrder, uploadTextWorkOrder } from "./workOrdersFunctions";
import { delayedAction } from "../../utils/Functions";
import { useAppDispatch } from "../../app/hooks";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";

export interface UploadStateProps {
    type: "error" | "warning" | "success" | "info";
    title: string;
    message: string;
}

export function ImportWalmartWorkOrderModal(props: ModalBase) {
    const { open, handleClose } = props;
    const dispatch: Dispatch<AnyAction> = useAppDispatch();
    const theme = useTheme();
    const [loading, setLoading] = useState<boolean>(false);
    const [files, setFiles] = useState<FileWithPath[]>([]);
    const [workOrderText, setWorkOrderText] = useState<string>("");
    const disableFileUpload = workOrderText.trim().length > 0;
    const [uploadState, setUploadState] = useState<UploadStateProps>({
        type: "error",
        message: "",
        title: "",
    });
    const [workOrderLink, setWorkOrderLink] = useState<string>("");

    const onDrop = useCallback((acceptedFiles: FileWithPath[]) => {
        setFiles(acceptedFiles);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        maxFiles: 1,
        disabled: disableFileUpload,
        accept: {
            "application/pdf": [".pdf"],
        },
    });

    function clearImportData() {
        setLoading(false);
        setFiles([]);
        setWorkOrderText("");
        setUploadState({ type: "error", title: "", message: "" });
        setWorkOrderLink("");
    }

    function onClose() {
        handleClose();
        delayedAction(500, clearImportData);
        delayedAction(500, () => setLoading(false));
    }

    function submitWorkOrderFile() {
        // eslint-disable-next-line no-undef
        const FormData = require("form-data");

        // Create a new form instance
        const form = new FormData();

        files.forEach((file) => {
            uploadPDFWorkOrder(form, file, dispatch, setUploadState, setWorkOrderLink);
        });
    }

    function submitWorkOrderText() {
        uploadTextWorkOrder(workOrderText, dispatch, setUploadState, setWorkOrderLink);
    }

    function onSubmit() {
        setLoading(true);
        if (files.length > 0) {
            submitWorkOrderFile();
        } else {
            submitWorkOrderText();
        }
    }

    const fileList = files.map((file: FileWithPath, index: number) => {
        let background = lighten(theme.palette.background.paper, 0.07);

        if (index % 2 === 0) {
            background = darken(background, 0.05);
        }
        return (
            <Box
                key={file.path}
                sx={{
                    display: "flex",
                    bgcolor: background,
                    p: 1,
                    alignItems: "center",
                }}
            >
                {file.path}
            </Box>
        );
    });

    const disableButtons: boolean = (!fileList || fileList.length < 1) && workOrderText.trim().length < 1;

    const disableTextField: boolean = fileList.length > 0;

    return (
        <Modal
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
            open={open}
            onClose={onClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 500,
            }}
        >
            <Fade in={open}>
                <Box
                    sx={{
                        width: "100%",
                        maxHeight: "90vh",
                        maxWidth: "800px",
                        backgroundColor: theme.palette.background.paper,
                        border: "1px solid #000",
                        boxShadow: theme.shadows[5],
                        p: 5,
                        margin: theme.spacing(2, 4, 3),
                        zIndex: 1000,
                        overflowY: "auto",
                        position: "relative",
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                        }}
                    >
                        <Tooltip title="Close">
                            <IconButton aria-label="delete" onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    {uploadState.message === "" && (
                        <>
                            <Typography variant="h5" sx={{ mb: 2, opacity: disableFileUpload ? 0.35 : 1.0 }}>
                                Import from Walmart PDF
                            </Typography>
                            <div {...getRootProps()}>
                                <Paper
                                    sx={{
                                        margin: "1, 0, 1, 0",
                                        flex: 1,
                                        display: fileList.length > 0 ? "none" : "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        padding: "20px",
                                        borderWidth: 2,
                                        borderRadius: 2,
                                        borderColor: "#eeeeee",
                                        borderStyle: "dashed",
                                        bgColor: "background.paper",
                                        color: "#bdbdbd",
                                        outline: "none",
                                        transition: "border .24s ease-in-out",
                                        cursor: disableFileUpload ? "not-allowed" : "pointer",
                                        opacity: disableFileUpload ? 0.5 : 1,
                                    }}
                                >
                                    <input {...getInputProps()} />
                                    {isDragActive ? (
                                        <Typography>Drop the PDF file here...</Typography>
                                    ) : (
                                        <Typography>Drag and drop PDF file, or click to select the file.</Typography>
                                    )}
                                </Paper>
                            </div>
                            <ConditionalBox hidden={!fileList || fileList.length < 1}>
                                <Paper elevation={0} sx={{ p: 1, mt: 1 }}>
                                    <Stack sx={{ mb: 3 }}>
                                        <Box sx={{ mb: 1 }}>
                                            <Typography variant="h6">Files</Typography>
                                            <Divider />
                                        </Box>
                                        {fileList}
                                    </Stack>
                                    <Alert severity="info">
                                        <Typography variant="caption">
                                            Processing PDF Work Orders may take up to a minute.
                                        </Typography>
                                    </Alert>
                                </Paper>
                            </ConditionalBox>
                            <Divider sx={{ mt: 3, mb: 3 }} />
                            <Typography variant="h5" sx={{ mb: 2, opacity: disableTextField ? 0.35 : 1.0 }}>
                                Import from Walmart Email
                            </Typography>
                            <Paper elevation={0}>
                                <TextField
                                    disabled={disableTextField}
                                    sx={{ opacity: disableTextField ? 0.35 : 1.0 }}
                                    value={workOrderText}
                                    fullWidth
                                    multiline
                                    variant="outlined"
                                    onChange={(event) => setWorkOrderText(event.target.value)}
                                    rows={5}
                                    placeholder="Paste work order text here."
                                />
                            </Paper>
                            <>
                                {!loading && (
                                    <Stack direction="row" spacing={2} width="100%" sx={{ mt: 3 }}>
                                        <Button
                                            disabled={disableButtons}
                                            onClick={clearImportData}
                                            variant="contained"
                                            color="secondary"
                                            fullWidth
                                        >
                                            Clear
                                        </Button>
                                        <Button
                                            disabled={disableButtons}
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            onClick={onSubmit}
                                        >
                                            Submit
                                        </Button>
                                    </Stack>
                                )}
                            </>
                            <>
                                {loading && (
                                    <Stack direction="row" justifyContent="center" sx={{ mt: 4, mb: 2 }}>
                                        <CircularProgress size={20} sx={{ mr: 2 }} />
                                    </Stack>
                                )}
                            </>
                        </>
                    )}
                    {uploadState.message !== "" && (
                        <>
                            <Box>
                                <Alert severity={uploadState.type}>
                                    <AlertTitle>{uploadState.title}</AlertTitle>
                                    <Typography>{uploadState.message}</Typography>
                                </Alert>
                            </Box>
                            <ConditionalBox
                                hidden={!workOrderLink}
                                sx={{
                                    mt: 1,
                                    mb: 1,
                                }}
                            >
                                <Link href={workOrderLink} target="_blank">
                                    Click here to view the work order.
                                </Link>
                            </ConditionalBox>
                            <Stack direction="row" spacing={2} width="100%" sx={{ mt: 3 }}>
                                <Button onClick={clearImportData} variant="contained" color="secondary" fullWidth>
                                    Clear
                                </Button>
                                <Button variant="contained" color="primary" fullWidth onClick={onClose}>
                                    Close
                                </Button>
                            </Stack>
                        </>
                    )}
                </Box>
            </Fade>
        </Modal>
    );
}
